// bluaIQ — TASK-WEB-26: CLIENT-view presentation copy for construction users
// (Codex post-closeout backlog: "copy pass para usuario de construcción").
//
// PRESENTATION ONLY. These helpers change how the client view DISPLAYS technical
// tokens that already survived the data-layer filter (deriveClientLines) — they
// never filter, add, or mutate payload data, never run for the auditor view
// (which keeps rendering the raw payload verbatim), and never touch contract
// v2/v3 shapes. The data layer (takeoffReadonlyDisplayViewModel.ts) must never
// import this module.
//
// Fallbacks are honest: an unknown evidence class renders as its prettified key
// (never dropped, never guessed into a friendlier claim), and an unmatched note
// keeps today's behavior exactly (original text + "Texto original del plano"
// chip — the Codex rule that raw evidence is labeled, never runtime-translated,
// stays intact; only KNOWN system-narration families are rewritten, by exact
// pattern).

import type { BilingualLabel } from '../../shared/websitePayloadContractV2'

/** Builder-language provenance labels for every evidence class the client view
 *  can see today (real generator classes + the synthetic owner-only test
 *  houses). user_truth/designation/trust_tier/classification sources never
 *  reach the client — the data layer drops them (isClientSafeSource). */
export const CLIENT_EVIDENCE_CLASS_LABELS: Record<string, BilingualLabel> = {
  // Real generator classes (C-1500/C-1800 payload families). The ES wording for
  // the first two is the dispatch's verbatim requirement.
  pdf_schedule: { en: 'From the plan schedule', es: 'Del schedule del plano' },
  plan_schedule: { en: 'From the plan schedule', es: 'Del schedule del plano' },
  derived_measurement_candidate: { en: 'Measured from the plans', es: 'Medido desde los planos' },
  plan_source_spec_text: { en: 'From the plan specifications', es: 'De las especificaciones del plano' },
  rule_engine_guide: { en: 'Calculated guide (proposal)', es: 'Guía calculada (propuesta)' },
  // Synthetic-fixture classes (same visible surface, owner-only test houses).
  rule_engine_proposal: { en: 'Calculated guide (proposal)', es: 'Guía calculada (propuesta)' },
  user_budget: { en: 'From your budget', es: 'De su presupuesto' },
  pdf_reading_in_progress: { en: 'Plan reading in progress', es: 'Lectura del plano en proceso' },
  plan_room_labels: { en: 'From the room labels on the plan', es: 'De las etiquetas de cuartos del plano' },
  plan_elevations: { en: 'From the plan elevations', es: 'De las elevaciones del plano' },
  // TASK-WEB-36: v6 generic-path evidence classes (takeoff engine —
  // generic_quantification_from_probe.py / generic_first_read_probe.py /
  // clean_new_source_composer_area_only_rooms.py). Builder language, honest to
  // what each class actually is; the auditor view still shows the raw class.
  first_read_probe_readability_report: {
    en: 'Plan readability check (first read)',
    es: 'Revisión de legibilidad del plano (primera lectura)',
  },
  columnar_schedule_text: { en: 'From the plan schedule (columns)', es: 'Del schedule del plano (columnas)' },
  printed_room_area_text: { en: 'Printed room area from the plan', es: 'Área del cuarto impresa en el plano' },
  // TASK-WEB-41: the post-70B demo re-export introduces the perimeter composer's
  // own evidence class. Without a mapping it would fall back to the prettified key
  // ("room perimeter candidate assembly") — honest, but not builder language.
  // Named for what it IS: a perimeter assembled from the plan, still a candidate.
  room_perimeter_candidate_assembly: {
    en: 'Room perimeter assembled from the plan (candidate)',
    es: 'Perímetro del cuarto armado desde el plano (candidato)',
  },
  // TASK-WEB-44: the v11 PAINT lane introduces BLOCK-STATE evidence classes.
  // Unlike every class above (which names a SOURCE), these name why a term is
  // blocked — and the old prettify fallback turned them into the pipeline jargon
  // Codex found in the client view ("blocked dependency chain", "blocked pending
  // printed height reread"). Named here in builder language.
  blocked_dependency_chain: {
    en: 'Blocked: waiting on another item',
    es: 'Bloqueado: depende de otra partida',
  },
  blocked_pending_printed_height_reread: {
    en: 'Blocked: printed height pending re-read',
    es: 'Bloqueado: altura impresa pendiente de relectura',
  },
}

/** Resolve the client-facing provenance label.
 *
 *  TASK-WEB-44: the old fallback prettified an unknown class (underscores →
 *  spaces). That was honest for SOURCE-shaped classes ("pdf schedule"), but the
 *  v11 paint lane ships STATE-shaped classes, and prettifying those is exactly
 *  how "blocked dependency chain" / "blocked pending printed height reread"
 *  reached the client view (Codex NEEDS FIX). Prettifying does not remove a
 *  technical slug — it only hides the underscores.
 *
 *  Unmapped classes now resolve to '' (the badge drops the label and keeps the
 *  real evidence text; see clientSourceBadgeText). Clean suppression, never an
 *  invented claim — and generic, so the next unmapped class from insulation /
 *  facade / roof cannot leak the way paint's did. The AUDITOR view never calls
 *  this: it renders the raw class verbatim. */
export function clientEvidenceClassLabel(evidenceClass: string, isEs: boolean): string {
  const known = CLIENT_EVIDENCE_CLASS_LABELS[evidenceClass]
  if (known) return isEs ? known.es : known.en
  return ''
}

// TASK-WEB-44 — GENERIC LAST-LINE GUARD (client view only).
//
// Codex NEEDS FIX on the v11 paint close-out: four internal tokens reached the
// CLIENT DOM for C-1500. Verified against the REAL production payload (canonical
// sha256 a33536771b2b2784…, 32 lines), read read-only from the production DB.
// Exactly ONE line is affected — idx 18, "Interior + exterior paint" — through
// THREE separate channels, which is why a per-token patch would not have held:
//
//   (1) evidence_class prettify  → `blocked_dependency_chain` and
//       `blocked_pending_printed_height_reread` rendered as "blocked dependency
//       chain" / "blocked pending printed height reread".
//   (2) source ref TEXT          → the WEB-26 heuristic treats a ref containing
//       whitespace as human evidence and renders it verbatim; this ref is a
//       human-shaped sentence that EMBEDS two slugs:
//       "término INTERIOR: bloqueado — paint_interior_blocked_by_drywall_dependency
//        ← causa printed_interior_height_pending_reread".
//   (3) note TEXT                → "(causa encadenada:
//       printed_interior_height_pending_reread — altura interior impresa
//       pendiente de relectura/verificación)".
//
// The command-centre requirement is that insulation / facade / roof must not
// repeat this cycle, so the fix is generic, not a list of four strings: curated
// builder copy where we have it, and a mechanical last line that guarantees no
// snake_case / machine-namespace token reaches the client DOM. The guard NEVER
// invents text — it substitutes curated copy, or falls back to the engine's own
// words, or removes the token and tidies the scaffolding it leaves behind.
// AUDITOR IS UNTOUCHED: it renders class, ref and note raw and verbatim, which
// is also the negative control in the tests.

/** snake_case pipeline identifiers, INCLUDING colon-namespaced ones.
 *
 *  HARDENED (Codex #473-#2, 2026-07-22). The earlier form matched only the
 *  snake_case core (`printed_area`, `label_only`) and left the colon namespace
 *  behind, so `room:label_only:BATHROOM 1` cleaned to `room::BATHROOM 1` — a
 *  visible internal residue. The pattern now also consumes an optional lowercase
 *  `namespace:` prefix chain and an optional lowercase `:segment` tail around the
 *  core, so `room:label_only`, `printed_area:` and `perimeter_candidate:` are
 *  removed whole.
 *
 *  The core STILL requires at least one underscore and a lowercase-letter start,
 *  which is exactly what keeps legitimate content safe: a roof pitch `3:12`, a
 *  dimension `60.0x32.667` and a range `20.2–24.0` are digit-led / underscore-free
 *  and never match. Prose never matches either (no underscores in ES/EN copy). */
const TECHNICAL_SLUG_RX = /\b(?:[a-z][a-z0-9]*:)*[a-z][a-z0-9]*(?:_[a-z0-9]+)+(?::[a-z0-9]+)*\b/g

/** "engine::stage" style machine namespaces. */
const MACHINE_NAMESPACE_RX = /\b[\w.-]+::[\w.-]+\b/g

/** Curated builder copy for KNOWN internal slugs, used when the guard has to
 *  substitute rather than suppress. Unmapped slugs are suppressed, never
 *  guessed. */
export const CLIENT_TECHNICAL_SLUG_COPY: Record<string, BilingualLabel> = {
  paint_interior_blocked_by_drywall_dependency: {
    es: 'pintura interior pendiente: depende de la tablaroca',
    en: 'interior paint pending: depends on drywall',
  },
  printed_interior_height_pending_reread: {
    es: 'la altura impresa está pendiente de relectura',
    en: 'the printed height is pending re-read',
  },
}

/** Curated whole-ref rewrites. A ref that is a human-shaped sentence built
 *  AROUND slugs reads badly if each slug is substituted in place, so the known
 *  v11 shapes get one clean builder sentence. Unmatched refs fall through to the
 *  mechanical guard below. */
const CLIENT_REF_REWRITES: Array<{ rx: RegExp; copy: BilingualLabel }> = [
  {
    // C-1500 v11, Paint / Pintura, sources[2].
    rx: /^t[ée]rmino\s+INTERIOR:\s*bloqueado\s*—\s*paint_interior_blocked_by_drywall_dependency\s*←\s*causa\s+printed_interior_height_pending_reread$/i,
    copy: {
      es: 'término interior pendiente: depende de la tablaroca, cuya altura impresa está pendiente de relectura',
      en: 'interior term pending: depends on drywall, whose printed height is pending re-read',
    },
  },
]

/** Remove every technical token from a CLIENT-bound string: curated copy when we
 *  have it, clean removal otherwise, then tidy the connectors ("—", "←", ":",
 *  empty parens) the removal orphaned. Returns '' when nothing meaningful is
 *  left, so the caller can suppress the element instead of rendering debris.
 *  Nothing is ever invented and no engine-authored word is translated. */
export function stripClientTechnicalSlugs(text: string, isEs: boolean): string {
  const substituted = text
    .replace(MACHINE_NAMESPACE_RX, '')
    .replace(TECHNICAL_SLUG_RX, (token) => {
      const curated = CLIENT_TECHNICAL_SLUG_COPY[token]
      return curated ? (isEs ? curated.es : curated.en) : ''
    })
  const tidied = substituted
    .replace(/\(\s*[)\]]/g, '')
    .replace(/\(\s*\)/g, '')
    .replace(/\s*[—–←→]\s*(?=$|[),;.])/g, '')
    .replace(/\s*:\s*(?=$|[),;.])/g, '')
    .replace(/\s*[—–←→]\s*[—–←→]\s*/g, ' — ')
    .replace(/\s+/g, ' ')
    .replace(/\s+([,;.)])/g, '$1')
    .replace(/[\s,;:—–←→]+$/g, '')
    .replace(/^[\s,;:—–←→]+/g, '')
    .trim()
  // Nothing but punctuation left → suppress rather than render debris.
  return /[\p{L}\p{N}]/u.test(tidied) ? tidied : ''
}

/** Client-view text for one source badge, with every guard applied. Returns null
 *  when neither a provenance label nor client-safe evidence text survives — the
 *  caller must then render no badge at all (an empty pill is debris too).
 *  AUDITOR CALLERS MUST NOT USE THIS: the auditor renders `class: ref` raw. */
export function clientSourceBadgeText(
  evidenceClass: string,
  ref: string,
  isEs: boolean,
): string | null {
  const label = clientEvidenceClassLabel(evidenceClass, isEs)
  let refText = ''
  if (!isMachineTokenRef(ref)) {
    const curated = CLIENT_REF_REWRITES.find((r) => r.rx.test(ref.trim()))
    refText = curated ? (isEs ? curated.copy.es : curated.copy.en) : stripClientTechnicalSlugs(ref, isEs)
  }
  // "<label>: <ref>" is the WEB-26 shape, but the v11 BLOCK-state labels are
  // themselves a clause with a colon ("Bloqueado: depende de otra partida"), and
  // "a: b: c" reads badly. Those compose with an em dash instead.
  if (label && refText) return label.includes(':') ? `${label} — ${refText}` : `${label}: ${refText}`
  if (label) return label
  if (refText) return refText
  return null
}

/** Internal pipeline identifiers (probe ids, "engine::stage" refs, versioned
 *  rule ids, synthetic fixture refs) carry no whitespace and mean nothing to a
 *  builder. The client badge hides THE TOKEN TEXT only — the source itself
 *  still renders (provenance label + page). Refs containing whitespace are
 *  real plan/derivation evidence and keep their text verbatim (never
 *  rewritten — raw evidence is labeled, not translated). */
export function isMachineTokenRef(ref: string): boolean {
  const t = ref.trim()
  return t.length === 0 || !/\s/.test(t)
}

// Curated builder rewrites for KNOWN single-language system notes the engine
// ships (system narration, NOT raw plan evidence). Exact families only, counts
// captured so the copy survives count changes — an unmatched note falls
// through to today's behavior unchanged.
const TRIM_REGISTRY_NOTE_RX =
  /^room registry excludes (\d+) suspect rooms? and (\d+) labels? without printed dimensions; derived LF understates accordingly$/i

// TASK-WEB-36: the v6 generic path emits BLOCKED notes with a fixed shape
// (takeoff engine _blocked_note): `BLOCKED:<slug> — <es>; nada fue inventado /
// <en>; nothing was invented`. This captures the slug plus the engine's own
// bilingual reason so the client view can show builder language while the
// auditor view keeps the raw note verbatim.
// TASK-WEB-39: the v7 composer APPENDS its own segments after the block reason
// ("…; nothing was invented; opciones listadas: 37.67 LF / 38.33 LF — …; estado
// del ensamble: perimeter_candidates_ambiguous"), so the original `$` anchor no
// longer matched and every v7 ambiguous line fell through to the raw text —
// leaking the `BLOCKED:<slug> —` token into the CLIENT view in prod, which the
// WEB-36 contract forbids. The tail is now optional and ignored for copy
// purposes: the ambiguity's own values are rendered separately from the raw note
// (deriveAmbiguousOptions), so nothing is lost by not echoing the tail here.
const BLOCKED_NOTE_RX =
  /^BLOCKED:([a-z0-9_]+) — (.+?); nada fue inventado \/ (.+?); nothing was invented(?:;.*)?$/i

// Curated builder rewrites for KNOWN block slugs (dispatch requirement:
// room_perimeter_not_printed). Any other slug falls back to the engine's OWN
// bilingual reason text (captured from the note) — honest, never invented.
const BLOCKED_SLUG_COPY: Record<string, BilingualLabel> = {
  room_perimeter_not_printed: {
    es: 'Bloqueado: el perímetro del cuarto no está impreso en el plano',
    en: 'Blocked: the room perimeter is not printed on the plan',
  },
}

/** Rewrite a KNOWN technical system note into builder language for the client
 *  view. Returns null for everything else — the caller must then render the
 *  note exactly as before (bilingual split / original text + chip). */
export function rewriteClientNote(note: string, isEs: boolean): string | null {
  const trimmed = note.trim()
  const m = TRIM_REGISTRY_NOTE_RX.exec(trimmed)
  if (m) {
    const suspects = Number(m[1])
    const noDims = Number(m[2])
    if (isEs) {
      return `El cálculo excluye ${suspects} ${suspects === 1 ? 'cuarto' : 'cuartos'} con datos por confirmar y ${noDims} ${noDims === 1 ? 'cuarto' : 'cuartos'} sin medidas impresas en el plano; el total puede quedarse corto.`
    }
    return `The calculation leaves out ${suspects} ${suspects === 1 ? 'room' : 'rooms'} with data still to confirm and ${noDims} ${noDims === 1 ? 'room' : 'rooms'} without printed measurements on the plan; the total may run short.`
  }
  // BLOCKED family: curated builder copy per slug, else the engine's own reason
  // text (honest fallback — the client never sees the raw `BLOCKED:slug —`
  // token, and nothing is invented: unmapped slugs reuse the engine's words).
  const b = BLOCKED_NOTE_RX.exec(trimmed)
  if (b) {
    const slug = b[1]
    const reasonEs = b[2].trim()
    const reasonEn = b[3].trim()
    const curated = BLOCKED_SLUG_COPY[slug]
    if (curated) return isEs ? curated.es : curated.en
    return isEs ? `Bloqueado: ${reasonEs}` : `Blocked: ${reasonEn}`
  }
  return null
}

// TASK-WEB-42: the v8 engine ships two families of note that carry INTERNAL
// pipeline vocabulary into text a builder reads. Both leaked to the CLIENT view
// in prod (Codex finding, non-blocking). Verified against the real payloads,
// read read-only from the production DB:
//
//  (a) C-1600 (canonical sha256 546f23830b9ec37f…, 35 lines, 9 of them affected)
//      "…no está confirmada como planta o schedule / …not confirmed as plan view
//       or schedule (estado view-aware: open_unresolved_view_ownership — jamás
//       arbitrado en silencio / view-aware state: open_unresolved_view_ownership
//       — never arbitrated silently)"
//      The technical state rides in a TRAILING parenthetical that is ITSELF a
//      bilingual pair. splitBilingualNote cuts each segment at its first
//      depth-0 " / ", so the whole parenthetical — both languages, slug twice —
//      lands in the ENGLISH half: EN leaked the slug AND showed raw Spanish.
//
//  (b) Spring Garden (canonical sha256 a99150256a60f0b0…, 45 lines, BATHROOM 205)
//      "…vanos de puerta sin descontar (user_truth: el rodapié no corre a través
//       de puertas) …/ …door openings not deducted (user_truth: baseboard does
//       not run across door openings) …"
//      Here the parenthetical's CONTENT is real builder information; only the
//      `user_truth:` provenance prefix is internal vocabulary. The data layer's
//      isClientSafeSource drops user_truth SOURCES but never inspected note TEXT,
//      which is why this survived every earlier pass.
//
// Both are rewritten BILINGUALLY (never per-language), so the note handed to
// splitBilingualNote still carries both halves and the language split downstream
// is unchanged. (a) is reshaped into a normal trailing segment — the engine's own
// words are kept, the prefix and slug are dropped; (b) keeps its content and
// loses only the prefix. Nothing is invented, nothing is translated at runtime,
// and no value is removed. CLIENT VIEW ONLY — the auditor keeps the raw note
// verbatim, slugs included, and every parser still reads line.notes.
const VIEW_AWARE_STATE_PAREN_RX =
  /\s*\(\s*estado view-aware:\s*[a-z0-9_]+\s*—\s*(.+?)\s+\/\s+view-aware state:\s*[a-z0-9_]+\s*—\s*(.+?)\s*\)/gi

// VAULTED-P1 recapture: the engine's re-export DROPPED the slug from the
// view-aware parenthetical and narrates the state in PROSE — same internal
// prefix, no machine token: "(estado view-aware: abierta, pertenencia de
// vistas sin resolver — … / view-aware state: open, view ownership
// unresolved — …)". Same family, same rule: keep the engine's own bilingual
// gloss, drop ONLY the prefix. Runs AFTER the slug form above, so slugged
// parentheticals are already gone when this one looks.
const VIEW_AWARE_STATE_PROSE_PAREN_RX =
  /\s*\(\s*estado view-aware:\s*(.+?)\s+\/\s+view-aware state:\s*(.+?)\s*\)/gi

const USER_TRUTH_PREFIX_RX = /\(\s*user_truth:\s*/gi

// TASK-WEB-44 rule — LEGACY-ONLY. Scoped to the v11 note shape, which names the
// blocker's cause slug and then immediately glosses it in prose:
//   "(causa encadenada: printed_interior_height_pending_reread — altura interior
//    impresa pendiente de relectura/verificación)"
// The gloss is honest builder text and it is the ENGINE'S OWN, so the rule keeps
// it and drops the "<label>: <slug> —" head. Structural and language-agnostic on
// purpose: it runs while the note is still bilingual (before splitBilingualNote),
// so it must not substitute language-specific copy.
//
// ── WHY IT IS NAMED "LEGACY" (TASK-WEB-44 mini guard, 2026-07-19) ────────────
// It does NOT fire on the v11.3 composer shape, which emits bilingual PROSE with
// no slug and no em-dash:
//   "(causa encadenada: <ES prose> / chained cause: <EN prose>)"
// That is deliberate and must stay deliberate: the v11.3 defect (ES leaking into
// the EN view, a dropped honesty clause, unbalanced parens) is an EMITTER bug and
// its fix belongs in the engine — see SPEC_COPY_FIX.md. Widening this regex to
// swallow the new shape would mask an emitter defect at the display layer, which
// is exactly what the sealed Confirmation Posture forbids. `envSelectiveRead`-style
// pins in takeoffClientCopy.smoke.ts (TCC #49/#50) assert BOTH halves of that: it
// still transforms the legacy shape, and it still does NOT match the v11.3 shape.
//
// ── WHY IT IS NOT RETIRED ───────────────────────────────────────────────────
// It is still load-bearing for the LIVE C-1500 v11 payload, whose note is pinned
// verbatim as V11_PAINT_NOTE_C1500 in takeoffClientCopy.smoke.ts (TCC #48). Until
// that project is re-exported/re-quantified onto a composer that no longer emits
// the slug shape, removing this rule would put `printed_interior_height_pending_reread`
// back in front of a client. Retirement criteria are recorded in the test file.
//
// ── ORDERING DEPENDENCY (do not reorder the .replace chain) ─────────────────
// This pattern's label part is broad (`[^():]{0,48}:`), so on its own it ALSO
// matches the view-aware family. It is only safe because VIEW_AWARE_STATE_PAREN_RX
// runs FIRST and consumes those. Measured on the bundled demo payload: the rule
// matches 9 lines in isolation, and 0 lines in true pipeline order. Reordering the
// chain would silently hand the view-aware family to this rule and drop its English
// half. TCC #51 pins that ordering.
const LEGACY_V11_CAUSE_SLUG_PAREN_RX =
  /\(\s*[^():]{0,48}:\s*[a-z][a-z0-9]*(?:_[a-z0-9]+)+\s*—\s*([^()]+?)\s*\)/gi

/** Strip KNOWN internal pipeline vocabulary out of a note for the CLIENT view,
 *  keeping the note bilingual and keeping every engine-authored word that means
 *  something to a builder. Unmatched notes are returned byte-identical, so any
 *  note family this doesn't know keeps today's behavior exactly.
 *
 *  CURATED RULES ONLY — deliberately. This runs while the note is still
 *  bilingual and BEFORE the BLOCKED family's own rewrite (rewriteClientNote
 *  parses the RAW note), so a blanket slug sweep here would corrupt
 *  `BLOCKED:<slug> —` notes and break the WEB-42 byte-identical contract. The
 *  generic last-line guard runs later, at the DOM boundary, on the
 *  active-language half — see stripClientTechnicalSlugs. */
export function scrubClientNoteTechnicalTokens(note: string): string {
  return note
    .replace(VIEW_AWARE_STATE_PAREN_RX, (_m, es: string, en: string) => `; ${es.trim()} / ${en.trim()}`)
    .replace(VIEW_AWARE_STATE_PROSE_PAREN_RX, (_m, es: string, en: string) => `; ${es.trim()} / ${en.trim()}`)
    .replace(USER_TRUTH_PREFIX_RX, '(')
    .replace(LEGACY_V11_CAUSE_SLUG_PAREN_RX, (_m, gloss: string) => `(${gloss.trim()})`)
}

// TASK-WEB-30: CLIENT-view section headers in the ACTIVE language only. The
// payload ships headers as combined bilingual strings ("Openings / Aberturas")
// — and inconsistently (some fixture headers are English-only "Windows", MEP
// headers are ES-first with a "· <sub>" tail, and "Drywall / Interior Board"
// isn't even a clean EN/ES split). A naive split-on-"/" would mis-pick on
// those, so — exactly as with WEB-26 — headers are resolved through an explicit
// curated map keyed by the RAW section string. Unmapped sections fall through
// to the raw payload value (honest fallback, never invented). This is display
// only: the raw section string stays the grouping key / test id / MEP-partition
// key everywhere else; only the rendered header text changes, and only in the
// client view (the auditor view keeps the raw combined header verbatim).
export const CLIENT_SECTION_LABELS: Record<string, BilingualLabel> = {
  // Real C-1500 payload sections.
  'Openings / Aberturas': { en: 'Openings', es: 'Aberturas' },
  'Interior Trim / Molduras': { en: 'Interior Trim', es: 'Molduras' },
  'Exterior / Envolvente': { en: 'Exterior', es: 'Envolvente' },
  // "Interior Board" in the raw is an English clarifier, not a Spanish half;
  // the section's line items read "Interior drywall (tablaroca)", so ES=Tablaroca.
  'Drywall / Interior Board': { en: 'Drywall', es: 'Tablaroca' },
  'Roofing / Techo': { en: 'Roofing', es: 'Techo' },
  'Insulation / Aislamiento': { en: 'Insulation', es: 'Aislamiento' },
  'Paint / Pintura': { en: 'Paint', es: 'Pintura' },
  // Real MEP sections: ES-first "MEP — … / MEP guides & proposals · <sub>".
  // Each active-language header drops the other language's half and keeps the
  // "· <sub>" grouping tail in that language.
  'MEP — Guías y propuestas / MEP guides & proposals · Eléctrico (guía por norma)': {
    en: 'MEP guides & proposals · Electrical (code-based guide)',
    es: 'MEP — Guías y propuestas · Eléctrico (guía por norma)',
  },
  'MEP — Guías y propuestas / MEP guides & proposals · Plomería (muebles y asignación)': {
    en: 'MEP guides & proposals · Plumbing (fixtures & allowance)',
    es: 'MEP — Guías y propuestas · Plomería (muebles y asignación)',
  },
  'MEP — Guías y propuestas / MEP guides & proposals · HVAC (propuesta Manual J)': {
    en: 'MEP guides & proposals · HVAC (Manual J proposal)',
    es: 'MEP — Guías y propuestas · HVAC (propuesta Manual J)',
  },
  // Synthetic fixture houses (Alpha/Beta) — owner-only QA. English-only and
  // ES-only raw headers, mapped for a clean client toggle.
  Windows: { en: 'Windows', es: 'Ventanas' },
  Doors: { en: 'Doors', es: 'Puertas' },
  Insulation: { en: 'Insulation', es: 'Aislamiento' },
  Drywall: { en: 'Drywall', es: 'Tablaroca' },
  'Interior trim': { en: 'Interior trim', es: 'Molduras' },
  'MEP — Guías y propuestas / Eléctrico': {
    en: 'MEP guides & proposals · Electrical',
    es: 'MEP — Guías y propuestas · Eléctrico',
  },
  'MEP — Guías y propuestas / Plomería': {
    en: 'MEP guides & proposals · Plumbing',
    es: 'MEP — Guías y propuestas · Plomería',
  },
  'MEP — Guías y propuestas / HVAC': {
    en: 'MEP guides & proposals · HVAC',
    es: 'MEP — Guías y propuestas · HVAC',
  },
  // TASK-WEB-34: GENERIC first-read-probe pipeline sections (FASE 3). These
  // are the raw combined headers the generic quantification path emits
  // (takeoff engine: generic_quantification_from_probe.py — "Generic trade
  // names, not project data"). Each is a clean "EN / ES" combined string, so
  // the split is unambiguous — but they go through the SAME curated map (never
  // a naive split) for consistency and so the auditor keeps the raw header
  // verbatim. Openings / Interior Trim / Roofing / Insulation / Paint from the
  // generic path already resolve via the entries above (identical raw strings).
  'Flooring / Pisos': { en: 'Flooring', es: 'Pisos' },
  'Ceilings / Cielos': { en: 'Ceilings', es: 'Cielos' },
  'Rooms pending / Cuartos pendientes': { en: 'Rooms pending', es: 'Cuartos pendientes' },
  // Generic-path Drywall header is ES "Tablaroca" (distinct raw string from the
  // C-1500 "Drywall / Interior Board" above; both display ES = Tablaroca).
  'Drywall / Tablaroca': { en: 'Drywall', es: 'Tablaroca' },
  'Siding / Fachada': { en: 'Siding', es: 'Fachada' },
  'Framing / Estructura': { en: 'Framing', es: 'Estructura' },
}

/** Resolve a client-view section header to the active language. Unmapped
 *  sections return the raw payload string unchanged (honest fallback — never
 *  invented, never dropped). Display only; callers keep the raw section string
 *  as the grouping/test/partition key. */
export function clientSectionLabel(section: string, isEs: boolean): string {
  const known = CLIENT_SECTION_LABELS[section]
  if (known) return isEs ? known.es : known.en
  return section
}

// ── SUMMARY-SHEET ENUMS — QA 2026-07-20, both production FAILs ──────────────
//
// The 390 QA in production failed the Summary sheet twice, and both failures
// were the SAME leak wearing two hats: the sheet printed payload enums RAW.
//   FAIL 2  Readiness status → `support_ready_with_warnings`
//           Notices          → `candidate_only_not_purchase_ready, no_final_…`
//
// WARNING_COPY previously lived inside TakeoffReadonlyDisplayShell.tsx, so the
// website had curated copy and the Excel — which cannot import a React
// component — had no way to reach it. It moved HERE so there is exactly ONE
// copy with two consumers, which is the same rule TEE #8 already seals for
// statuses: "the website already owns this copy; a local map forks the voice."
//
// Readiness had NO curated copy anywhere: the website never renders
// website_readiness_status as text, so nothing existed to reuse. It is authored
// here once, for both surfaces, rather than a second time inside the exporter.

/** Client-facing copy for `payload.warnings[]` enums. */
export const CLIENT_WARNING_COPY: Record<string, BilingualLabel> = {
  candidate_only_not_purchase_ready: {
    en: 'Every quantity here is a candidate — none is ready for purchase.',
    es: 'Cada cantidad aquí es candidata — ninguna está lista para compra.',
  },
  no_final_quantity: {
    en: 'No quantity shown is final.',
    es: 'Ninguna cantidad mostrada es definitiva.',
  },
  no_purchase_bom_boq: {
    en: 'This is not a Bill of Materials or Bill of Quantities.',
    es: 'Esto no es una Lista de Materiales (BOM) ni un Cómputo de Cantidades (BOQ).',
  },
  not_an_order_price: {
    en: 'No price shown here is an order price.',
    es: 'Ningún precio mostrado aquí es un precio de orden.',
  },
  website_readonly_display: {
    en: 'This is a read-only display — nothing here can be edited or submitted.',
    es: 'Esta es una vista de solo lectura — nada aquí se puede editar ni enviar.',
  },
}

/** Client-facing copy for `payload.website_readiness_status`. */
export const CLIENT_READINESS_COPY: Record<string, BilingualLabel> = {
  support_ready_with_warnings: {
    en: 'Ready to support review — read the notices below first.',
    es: 'Listo para apoyar la revisión — lea primero los avisos de abajo.',
  },
  candidate_only_preview: {
    en: 'Preview of candidate quantities only.',
    es: 'Vista previa de cantidades candidatas únicamente.',
  },
}

/**
 * Resolve a warning enum to client copy, or `null` when none is curated.
 *
 * RETURNS NULL RATHER THAN THE SLUG, deliberately, and this is the one place
 * the module departs from its own honest-fallback convention (an unknown
 * evidence class renders prettified; an unknown warning renders nothing).
 *
 * The reason is that the two cases fail differently. A prettified evidence
 * class is still readable English. A raw warning enum is the exact string the
 * QA rejected, and printing it is the defect. So the caller gets null and
 * decides — and `assertEveryEnumHasClientCopy` below makes sure an unknown
 * enum breaks the test suite instead of silently vanishing from a client
 * document. Fail loudly in CI, never quietly in a builder's hands.
 */
export function clientWarningCopy(warning: string, isEs: boolean): string | null {
  const known = CLIENT_WARNING_COPY[warning]
  return known ? (isEs ? known.es : known.en) : null
}

/** Resolve a readiness enum to client copy, or `null` when none is curated.
 *  Same null-not-slug contract as `clientWarningCopy`. */
export function clientReadinessCopy(status: string, isEs: boolean): string | null {
  const known = CLIENT_READINESS_COPY[status]
  return known ? (isEs ? known.es : known.en) : null
}

// ── EXCEL v2 — sheet-2 two-state vocabulary + blocking-kind copy ─────────────
//
// SPEC_EXCEL_EXPORT_V2.md §2 (sealed). Cuantificación (sheet 2) collapses the
// full v1 status vocabulary into EXACTLY two states, on purpose and lossily: a
// reader pricing a job only needs "is this measured or not", and the sheet
// carries a pointer to Detalle for anyone who needs which of the four pending
// states a line is in (§2 R-2.b). Authored HERE once (R-2.c), never inline in
// the exporter — the same one-copy-two-consumers rule WARNING_COPY moved here
// under (PR #456): the exporter and any future surface read the SAME strings.
//
// The `—` for a missing quantity is the anti-0 gate made visible (§2 R-2.a):
// `0` is a measurement claim and it is false, so an unmeasured cell renders `—`.

/** The two sealed sheet-2 status strings — §2. `measured` is true when the line
 *  carries a measurement (not a pending status); false renders "Falta
 *  información" / "Information needed". Deliberately lossy — see the section
 *  header and the Detalle pointer below. */
export function clientMeasuredStateLabel(measured: boolean, isEs: boolean): string {
  if (measured) return isEs ? 'Con medida' : 'Measured'
  return isEs ? 'Falta información' : 'Information needed'
}

/** The missing-quantity cell — §2 R-2.a. Never `0`. Language-agnostic, but
 *  routed through copy so the anti-0 gate has ONE authored source. */
export const CLIENT_MISSING_QUANTITY = '—'

/** Client-facing copy for `line.client_blocking_kind` — the v11.6 field that
 *  names WHY a partida is blocked pending the client's input. Orthogonal to
 *  measurement: a blocked line can still carry a candidate quantity, and it must
 *  stay VISIBLY blocked with its reason (non-negotiable invariant). Sentences,
 *  never the raw enum — same null-not-slug contract as CLIENT_WARNING_COPY, and
 *  `missingClientBlockingKinds` below fails CI if the engine ships a new kind. */
export const CLIENT_BLOCKING_KIND_COPY: Record<string, BilingualLabel> = {
  plan_missing_information: {
    es: 'El plano no tiene la información necesaria para medir esta partida.',
    en: 'The plan does not have the information needed to measure this item.',
  },
  // Codex (cielo abovedado audit): this enum blocks ONE PARTIDA, not the whole
  // lane — a line-level `client_blocking_kind` on an otherwise-measured trade.
  // The old sentences said "Esta especialidad…" / "This trade…", which read as
  // a whole-lane outage on a single blocked item. Whole-lane blocks do not
  // route through this record — this copy is line-scoped by construction
  // (clientBlockingKindCopy is only called with line.client_blocking_kind).
  lane_not_available: {
    es: 'Esta partida todavía no está disponible para medición.',
    en: 'This item is not yet available for measurement.',
  },
}

/** Resolve a blocking kind to client copy, or `null` for `null`/unknown. A
 *  `null` kind is "not blocked" and yields nothing; an UNKNOWN kind also yields
 *  null (never the raw enum) and is caught loudly by missingClientBlockingKinds
 *  in CI — fail in the pipeline, never quietly in a builder's hands. */
export function clientBlockingKindCopy(kind: unknown, isEs: boolean): string | null {
  if (typeof kind !== 'string' || kind.length === 0) return null
  const known = CLIENT_BLOCKING_KIND_COPY[kind]
  return known ? (isEs ? known.es : known.en) : null
}

/** A blocked line that is STRUCTURALLY linked (by `client_question_ids`) to the
 *  real `client_question`s that block it. Renders instead of the generic
 *  `client_blocking_kind` sentence so a line with pending questions no longer
 *  reads as the contradiction "this trade is not available" while two real
 *  questions about it sit on the «Open questions» sheet (v11.7 emitter fix).
 *  `count` is the number of RESOLVED linked questions — never inferred, never a
 *  raw id. Points the reader at the Preguntas sheet. */
export function clientBlockedPendingQuestions(count: number, isEs: boolean): string {
  if (isEs) {
    const q = count === 1 ? 'esta pregunta' : `estas ${count} preguntas`
    return `Bloqueada — pendiente de ${q} (ver la hoja «Preguntas pendientes»).`
  }
  const q = count === 1 ? 'this question' : `these ${count} questions`
  return `Blocked — pending ${q} (see the «Open questions» sheet).`
}

/** Website variant of clientBlockedPendingQuestions: SAME precedence, NO
 *  locational pointer. The Excel sentence points at the «Preguntas pendientes»
 *  SHEET, which always exists in the workbook; on the website the questions
 *  section is FLAG-GATED (default-OFF, per-account), so pointing the reader at
 *  UI that may not render would be its own contradiction. The sentence states
 *  the fact and stops. */
export function clientBlockedPendingQuestionsInline(count: number, isEs: boolean): string {
  if (isEs) {
    const q = count === 1 ? 'una pregunta' : `${count} preguntas`
    return `Bloqueada — pendiente de ${q} para el cliente.`
  }
  const q = count === 1 ? 'one client question' : `${count} client questions`
  return `Blocked — pending ${q}.`
}

/** Sheet-2 → Detalle pointer for the lossy two-state (§2 R-2.b). The sheet MUST
 *  say that its status column is a two-state summary and where the full detail
 *  lives. */
export function clientDetailSheetNotice(isEs: boolean): string {
  return isEs
    ? 'El estado por partida se resume aquí en dos estados. El desglose por cuarto o por marca y su evidencia están en la hoja «Detalle».'
    : 'Item status is summarized here in two states. The room-by-room or mark-by-mark breakdown and its evidence are on the «Detail» sheet.'
}

/** Per-row Respaldo pointer to Detalle for a summary line that HAS detail
 *  children (§4 R-4.b — provenance Cuantificación → Detalle). The count is
 *  row-counting rendered as a SENTENCE, never a totalizable number (the one
 *  arithmetic the exporter is allowed, per v1 §4.4). */
export function clientDetailRowPointer(childCount: number, isEs: boolean): string {
  if (isEs) return `Ver ${childCount} ${childCount === 1 ? 'línea' : 'líneas'} en la hoja «Detalle»`
  return `See ${childCount} ${childCount === 1 ? 'line' : 'lines'} on the «Detail» sheet`
}

/**
 * Enumerate `client_blocking_kind` values present on the lines that have NO
 * curated client copy. The blocking-kind analogue of missingClientCopyEnums:
 * a new engine kind fails the suite over the real fixtures, so someone writes
 * the sentence rather than letting a blocked line lose its reason (or leak the
 * raw enum). `null` is "not blocked" and is never reported.
 */
export function missingClientBlockingKinds(
  lines: ReadonlyArray<{ client_blocking_kind?: unknown }>,
): string[] {
  const missing = new Set<string>()
  for (const line of lines) {
    const kind = line.client_blocking_kind
    if (typeof kind === 'string' && kind.length > 0 && !CLIENT_BLOCKING_KIND_COPY[kind]) {
      missing.add(kind)
    }
  }
  return [...missing]
}

// ── client_question CARD CHROME — spec §5, SPEC_CLIENT_QUESTION_UI_V1.md ─────
//
// Sealed contract name: `insulation-lane-checkpoint-v1`. This is the card's
// "chrome" copy: badges, the honest subtitle, the reversibility key line, the
// block-link sentence, the "noted" line, the pending-questions badge, the empty
// state, and the owner-criteria provenance phrase. It lives HERE (not inside the
// card component) for the same one-copy-two-consumers reason WARNING_COPY moved
// here (TEE #8): the Excel Sheet-3 empty state and this card converge on the
// same voice, so a local map inside the component would fork it.
//
// It is NOT the question CONTENT. `question_text`, `options[].label` and
// `suggested_interpretation` arrive `{es,en}` FROM THE ENGINE and are rendered
// verbatim, never rewritten (spec §5.1). This chrome never touches them.
//
// RATIFICATION (Codex audit of PR #467, 2026-07-21). Codex ratified the EN of
// badgePending/badgeAnswered/evidenceLead/suggestionLabel/sectionTitle and the
// owner-criteria phrase, and ORDERED the exact wording of honestSubtitle,
// blockLink and the empty state (the four corrections in the NEEDS FIX). Those
// ordered strings replace the earlier authored ones verbatim below; the ES is
// the ratified mockup wording throughout.
export const CLIENT_QUESTION_COPY = {
  // (a) status badges. #1/#2 follow the house word "Pending"/"Pendientes"
  //     already sealed as `pendingFirst` in the shell (spec §5.2).
  badgePending: { en: 'Pending question', es: 'Pregunta pendiente' }, // RATIFIED (Codex #467)
  badgeAnswered: { en: 'Answered', es: 'Respondida' }, //               RATIFIED (Codex #467)
  // (c) honest subtitle. Codex correction 4: the old "dos valores distintos"
  //     subtitle asserted a contradiction that is FALSE for sg-q-002 (which
  //     asks about ABSENT information, not two conflicting values). Replaced by
  //     Codex's ordered wording, which is true for both the conflict and the
  //     missing-info cases.
  honestSubtitle: {
    en: 'We need your answer before this item can be completed.',
    es: 'Necesitamos su respuesta antes de completar esta partida.',
  }, //                                                                 ORDERED by Codex (#467)
  // (e) evidence box lead-in (authored chrome — labels the curated quote).
  evidenceLead: { en: 'What the plan shows', es: 'Lo que dice el plano' }, // RATIFIED (Codex #467)
  // (f) block link — renders from field 9's PRESENCE, never its raw prose
  //     (spec §2.-1). Codex correction 4: "bloqueada en su presupuesto" mixed
  //     quantification with valuation — the block is a TAKEOFF/cuantificación
  //     state, not a budget one. Replaced by Codex's ordered wording.
  blockLink: {
    en: 'Until you answer, this item remains blocked in the takeoff.',
    es: 'Mientras no responda, esta partida permanece bloqueada en la cuantificación.',
  }, //                                                                 ORDERED by Codex (#467)
  // (g)/§4.1 the reversibility key line. Codex correction 1: this promise may
  //     only appear AFTER an answer is successfully saved. No write path exists
  //     in this read-only module, so the CARD does not render it in v1 — the
  //     string is kept here for when the write flow lands (must not be
  //     shortened/dropped then — spec §4.1).
  reversibilityLine: {
    en: 'Your answer is saved as a rule for this project — you can change it whenever you want',
    es: 'Su respuesta queda guardada como regla de este proyecto — puede cambiarla cuando quiera',
  }, //                                                                 POST-SAVE ONLY (Codex #467)
  // (h) "other / I don't know" acknowledgement. Codex correction 1: shown only
  //     AFTER a successful save — not rendered in v1 (no write path).
  noted: {
    en: 'Noted — the team will review it with you',
    es: 'Anotado — el equipo lo revisará con usted',
  }, //                                                                 POST-SAVE ONLY (Codex #467)
  // suggested_interpretation label — makes the NEVER-preselected rule legible.
  suggestionLabel: { en: 'Suggestion — you decide', es: 'Sugerencia — usted decide' }, // RATIFIED (Codex #467)
  // Block heading for the whole card list.
  sectionTitle: {
    en: 'Questions awaiting your decision',
    es: 'Preguntas pendientes de su decisión',
  }, //                                                                 RATIFIED (Codex #467)
} as const

/** Resolve one client_question chrome string to the active language. */
export function clientQuestionCopy(key: keyof typeof CLIENT_QUESTION_COPY, isEs: boolean): string {
  const c = CLIENT_QUESTION_COPY[key]
  return isEs ? c.es : c.en
}

/** RATIFIED (Codex #467). The client-facing provenance for a question/suggestion
 *  built on the owner's criteria. The client sees THIS fixed short phrase; the
 *  auditor keeps and verifies the full verbatim `source_user_truth` through its
 *  own channel. The raw value (e.g. "UT-7") NEVER reaches the client card. */
export function clientQuestionSourcePhrase(isEs: boolean): string {
  return isEs ? 'basado en criterio de la propietaria' : "based on the owner's criteria"
}

/** (i) Project summary badge — "N pending questions", singular-aware. */
export function clientQuestionPendingBadge(count: number, isEs: boolean): string {
  if (isEs) return `${count} ${count === 1 ? 'pregunta pendiente' : 'preguntas pendientes'}`
  return `${count} ${count === 1 ? 'pending question' : 'pending questions'}`
}

/** Empty state — spec §6. Condition 6: no structure = say so, NEVER a regex over
 *  prose. Codex correction 4 (#467): the earlier wording said "arriba" (above),
 *  but the quantity detail renders AFTER this section, not above it — a false
 *  spatial pointer. Replaced by Codex's ordered wording, which points at the
 *  quantity detail without asserting a position. */
export function clientQuestionEmptyState(isEs: boolean): string {
  return isEs
    ? 'No hay preguntas estructuradas pendientes en este documento. Las partidas bloqueadas y sus motivos aparecen en el detalle de cantidades.'
    : 'There are no pending structured questions in this document. Blocked items and their reasons appear in the quantity detail.'
}

/**
 * Enumerate payload enums that have NO curated client copy.
 *
 * Exists so "we dropped it silently" can never be the outcome of an engine
 * adding a warning. Tests call this over every real fixture; a new enum fails
 * the suite and someone has to write the copy. This is the no-silent-drop rule
 * applied to copy instead of to quantities.
 */
export function missingClientCopyEnums(input: {
  warnings?: unknown
  website_readiness_status?: unknown
}): string[] {
  const missing: string[] = []
  const warnings = Array.isArray(input.warnings) ? input.warnings : []
  for (const w of warnings) {
    if (typeof w === 'string' && !CLIENT_WARNING_COPY[w]) missing.push(w)
  }
  const status = input.website_readiness_status
  if (typeof status === 'string' && status && !CLIENT_READINESS_COPY[status]) {
    missing.push(status)
  }
  return missing
}
